import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import LanguageSwitcher from "./LanguageSwitcher";
import DarkModeToggle from "./DarkModeToggle";
import { LogoWordmark } from "./Logo";
import { useState } from "react";
import { Menu, X, ChevronDown, Users, Hammer, SprayCan, FileText } from "lucide-react";

// Long-tail SEO / service landing pages we want the visitor to discover
// from the top nav without cluttering the main "Leistungen" anchor.
const SERVICE_MENU = [
  { icon: Users,    label: "2 Mann Handling & Möbelmontage", desc: "Ab €59 — Sofas, Küchen, Elektrogeräte",  to: "/2-mann-handling" },
  { icon: Hammer,   label: "Küchenmontage",                    desc: "Ab €399 — Meisterbetrieb + Anschluss",   to: "/kuechenmontage" },
  { icon: SprayCan, label: "Graffiti-Entfernung",               desc: "Fassadenschutz + Anti-Graffiti-Coating", to: "/leistungen/graffiti-entfernung-hamburg" },
  { icon: FileText, label: "Fallstudien & Referenzen",          desc: "LKQ · PV Automotive · TIER · Dott",     to: "/case-studies" },
];

export default function Navbar() {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 w-full backdrop-blur-xl bg-white/80 border-b border-neutral-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="group" data-testid="brand-link">
          <LogoWordmark />
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm font-semibold">
          <div className="relative group">
            <a
              href="/#services"
              className="link-sweep transition-colors inline-flex items-center gap-1"
              data-testid="nav-services"
            >
              {t.nav.services}
              <ChevronDown size={13} className="opacity-60 group-hover:rotate-180 transition-transform" />
            </a>
            <div
              className="invisible opacity-0 group-hover:visible group-hover:opacity-100 absolute top-full start-0 mt-2 w-[380px] bg-white border border-neutral-200 shadow-2xl p-2 transition-all"
              data-testid="nav-services-dropdown"
            >
              {SERVICE_MENU.map(({ icon: Icon, label, desc, to }) => (
                <Link
                  key={to}
                  to={to}
                  className="flex items-start gap-3 p-3 hover:bg-neutral-50 group/item"
                  data-testid={`nav-services-item-${to.split("/").pop()}`}
                >
                  <div className="w-9 h-9 flex-shrink-0 bg-[#FF5A00]/10 text-[#FF5A00] flex items-center justify-center group-hover/item:bg-[#FF5A00] group-hover/item:text-white transition-colors">
                    <Icon size={16} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-black tracking-tight text-neutral-900">{label}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">{desc}</div>
                  </div>
                </Link>
              ))}
              <div className="border-t border-neutral-100 mt-1 pt-2 px-3 pb-1">
                <a
                  href="/#services"
                  className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 hover:text-[#FF5A00]"
                  data-testid="nav-services-view-all"
                >
                  Alle Leistungen ansehen →
                </a>
              </div>
            </div>
          </div>
          <Link to="/b2b" className="link-sweep transition-colors font-bold text-[#FF5A00]" data-testid="nav-b2b">{t.nav.b2b}</Link>
          <a href="/#about" className="link-sweep transition-colors" data-testid="nav-about">{t.nav.about}</a>
          <Link to="/blog" className="link-sweep transition-colors" data-testid="nav-blog">{t.nav.blog}</Link>
          <a href="/#contact" className="link-sweep transition-colors" data-testid="nav-contact">{t.nav.contact}</a>
          {user && <Link to="/dashboard" className="link-sweep transition-colors" data-testid="nav-dashboard">{t.nav.dashboard}</Link>}
          {user?.is_admin && <Link to="/admin" className="link-sweep transition-colors" data-testid="nav-admin">{t.nav.admin}</Link>}
        </nav>

        <div className="flex items-center gap-3">
          <DarkModeToggle />
          <LanguageSwitcher />
          {user ? (
            <>
              <Link to="/book" className="hidden sm:inline-flex btn-primary text-sm py-2 px-4" data-testid="nav-book-btn">{t.nav.book}</Link>
              <button onClick={handleLogout} className="hidden sm:inline-block text-sm font-semibold hover:text-[#FF5A00]" data-testid="nav-logout-btn">{t.nav.logout}</button>
            </>
          ) : (
            <Link to="/login" className="hidden sm:inline-flex btn-primary text-sm py-2 px-4" data-testid="nav-login-btn">{t.nav.login}</Link>
          )}
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-neutral-200 px-6 py-4 flex flex-col gap-3 bg-white">
          <a href="/#services" onClick={() => setOpen(false)} className="font-semibold">{t.nav.services}</a>
          <Link to="/b2b" onClick={() => setOpen(false)} className="font-bold text-[#FF5A00]">{t.nav.b2b}</Link>
          <a href="/#about" onClick={() => setOpen(false)} className="font-semibold">{t.nav.about}</a>
          <Link to="/blog" onClick={() => setOpen(false)} className="font-semibold">{t.nav.blog}</Link>
          <a href="/#contact" onClick={() => setOpen(false)} className="font-semibold">{t.nav.contact}</a>
          {user && <Link to="/dashboard" onClick={() => setOpen(false)} className="font-semibold">{t.nav.dashboard}</Link>}
          {user?.is_admin && <Link to="/admin" onClick={() => setOpen(false)} className="font-semibold">{t.nav.admin}</Link>}
          {user ? (
            <>
              <Link to="/book" onClick={() => setOpen(false)} className="btn-primary text-sm justify-center">{t.nav.book}</Link>
              <button onClick={handleLogout} className="text-start font-semibold">{t.nav.logout}</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="btn-primary text-sm justify-center">{t.nav.login}</Link>
          )}
        </div>
      )}
    </header>
  );
}
